import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Building2, CheckCircle2, Clock3, Search, ShieldAlert } from "lucide-react";
import { useNavigate } from "react-router-dom";

const STATUS = {
    structured_ready: { label: "Scheda strutturata", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
    intake_ready: { label: "Raccolta chat disponibile", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
    catalogued: { label: "Catalogata · workflow da implementare", cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock3 },
};

export default function ComunicaCatalog() {
    const [catalog, setCatalog] = useState(null);
    const [query, setQuery] = useState("");
    const [family, setFamily] = useState("all");
    const [selected, setSelected] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/comunica/catalog").then((r) => setCatalog(r.data));
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (catalog?.practice_types || []).filter((p) => {
            const inFamily = family === "all" || p.family === family;
            const haystack = `${p.name} ${p.code} ${(p.intents || []).join(" ")}`.toLowerCase();
            return inFamily && (!q || haystack.includes(q));
        });
    }, [catalog, family, query]);

    if (!catalog) return <div className="p-10 text-sm text-muted-foreground">Caricamento catalogo…</div>;

    const startChat = (item) => {
        sessionStorage.setItem("comunica_catalog_prompt", `Voglio predisporre questa pratica: ${item.name}. Guidami nella raccolta dei dati e crea una bozza COMUNICA_RI con practice_type \"${item.code}\".`);
        navigate("/chat");
    };

    return (
        <div className="p-6 lg:p-10 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-8">
                <div>
                    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-primary mb-2"><Building2 size={14} /> Registro Imprese</div>
                    <h1 className="font-display text-3xl font-medium">Catalogo pratiche ComUnica</h1>
                    <p className="text-sm text-muted-foreground mt-2">Catalogo {catalog.catalog_version} · target Fedra {catalog.fedra_target} · {catalog.practice_types.length} tipologie</p>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-xs">
                    <ShieldAlert size={15} /> Ambiente Lab: nessun invio esterno
                </div>
            </div>

            <div className="grid md:grid-cols-[1fr_260px] gap-3 mb-7">
                <label className="relative">
                    <Search size={16} className="absolute left-3 top-3 text-muted-foreground" />
                    <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca: sede, ATECO, soci, bilancio, INPS, SUAP…" className="w-full pl-10 pr-3 py-2.5 rounded-md border border-border bg-card text-sm outline-none focus:ring-2 focus:ring-primary/20" />
                </label>
                <select value={family} onChange={(e) => setFamily(e.target.value)} className="px-3 py-2.5 rounded-md border border-border bg-card text-sm">
                    <option value="all">Tutte le famiglie</option>
                    {catalog.families.map((f) => <option key={f.code} value={f.code}>{f.name}</option>)}
                </select>
            </div>

            <div className="space-y-8">
                {catalog.families.filter((f) => family === "all" || family === f.code).map((f) => {
                    const items = filtered.filter((p) => p.family === f.code);
                    if (!items.length) return null;
                    return <section key={f.code}>
                        <h2 className="text-sm font-bold uppercase tracking-wider mb-3">{f.name} <span className="text-muted-foreground font-normal">({items.length})</span></h2>
                        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
                            {items.map((p) => {
                                const meta = STATUS[p.status] || STATUS.catalogued;
                                const Icon = meta.icon;
                                return <article key={p.code} className="rounded-lg border border-border bg-card p-4 flex flex-col min-h-48">
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono mb-2">{p.code}</div>
                                    <h3 className="font-semibold text-sm leading-snug">{p.name}</h3>
                                    <div className="mt-3 flex flex-wrap gap-1.5">
                                        {(p.recipients || []).map((r) => <span key={r} className="text-[10px] px-2 py-0.5 rounded-full bg-accent">{r}</span>)}
                                    </div>
                                    <div className={`mt-3 inline-flex self-start items-center gap-1.5 text-[10px] border rounded-full px-2 py-1 ${meta.cls}`}><Icon size={11} /> {meta.label}</div>
                                    <div className="mt-3 text-[11px] text-muted-foreground">
                                        {p.sections?.length || 0} sezioni · {(p.sections || []).reduce((n, s) => n + (s.fields?.length || 0), 0)} campi · {p.required_documents?.length || 0} gruppi documentali
                                    </div>
                                    <div className="mt-auto pt-4 grid grid-cols-2 gap-2">
                                        <button onClick={() => setSelected(p)} className="px-3 py-2 rounded-md border border-border text-xs font-semibold hover:bg-accent">Vedi struttura</button>
                                        <button onClick={() => startChat(p)} className="w-full px-3 py-2 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90">Avvia raccolta in chat</button>
                                    </div>
                                </article>;
                            })}
                        </div>
                    </section>;
                })}
            </div>
            {selected && <WorkflowDetail item={selected} onClose={() => setSelected(null)} onStart={() => startChat(selected)} />}
        </div>
    );
}

function WorkflowDetail({ item, onClose, onStart }) {
    return <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onMouseDown={onClose}>
        <div className="w-full max-w-4xl bg-background rounded-xl border border-border shadow-xl my-6" onMouseDown={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-border flex items-start justify-between gap-4">
                <div><div className="text-[10px] font-mono text-muted-foreground">{item.code}</div><h2 className="font-display text-2xl mt-1">{item.name}</h2><p className="text-xs text-muted-foreground mt-1">Profilo {item.profile} · generazione {item.generation_status} · invio {item.submission_status}</p></div>
                <button onClick={onClose} className="px-3 py-1.5 rounded-md border border-border text-xs">Chiudi</button>
            </div>
            <div className="p-5 space-y-6">
                <section><h3 className="text-sm font-bold mb-2">Documenti da raccogliere</h3><div className="flex flex-wrap gap-2">{item.required_documents.map((d) => <span key={d} className="text-xs bg-accent rounded-full px-3 py-1">{d.replaceAll("_", " ")}</span>)}</div></section>
                <section><h3 className="text-sm font-bold mb-2">Controlli preliminari</h3><ul className="list-disc pl-5 text-xs text-muted-foreground space-y-1">{item.preliminary_rules.map((r) => <li key={r}>{r.replaceAll("_", " ")}</li>)}</ul></section>
                <section><h3 className="text-sm font-bold mb-3">Sezioni della scheda</h3><div className="grid md:grid-cols-2 gap-3">{item.sections.map((s) => <div key={s.code} className="rounded-lg border border-border p-3"><div className="font-semibold text-sm">{s.title}{s.repeatable ? " · ripetibile" : ""}</div><ul className="mt-2 space-y-1">{s.fields.map((f) => <li key={f.path} className="text-xs text-muted-foreground flex justify-between gap-2"><span>{f.label}</span>{f.required && <span className="text-primary font-semibold">obbligatorio</span>}</li>)}</ul></div>)}</div></section>
            </div>
            <div className="p-5 border-t border-border flex justify-end"><button onClick={onStart} className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold">Avvia raccolta guidata</button></div>
        </div>
    </div>;
}
